import { ReactNode, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../../../services/api';
import { Lead, Panel, PanelHeader, PanelBody } from '../../ui/Panel';
import Button from '../../ui/Button';
import Pill from '../../ui/Pill';
import Modal from '../../ui/Modal';
import MoreMenu from '../../ui/MoreMenu';
import { KpiBand } from '../../ui/Kpi';
import { ErrorNotice, InfoNotice, SkeletonRows } from '../../ui/States';
import { errorMessage, inputClass, money, toCents } from '../../../lib/programs';
import { moneyKey } from '../../../lib/eventMoney';
import type { EventDetail } from '../../../lib/events';
import {
  Comp,
  KIND_LABEL,
  MEAL_LABEL,
  OutreachList,
  Partner,
  PreparedOutreach,
  Registrant,
  Registration,
  SendEntry,
  TicketType,
  WINDOW_LABEL,
  dayText,
  downloadText,
} from '../../../lib/eventRegistration';
import type { SimplePanelProps } from '../adapt';
import { em, headRow, num, td, th } from '../logistics';
import { confirmAction, promptAction } from '../../ui/Confirm';
import AddLink from '../../ui/AddLink';

const key = (eventId: string) => ['event-registration', eventId];

export function useRegistration(eventId: string) {
  return useQuery<Registration>(key(eventId), async () => (await api.get(`/events/${eventId}/registration`)).data.data);
}

function useRefresh(eventId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries(key(eventId));
    queryClient.invalidateQueries(moneyKey(eventId));
  };
}

function useEventDays(eventId: string) {
  const event = useQuery<EventDetail>(['event', eventId], async () => (await api.get(`/events/${eventId}`)).data.data);
  return (event.data?.event.days ?? []).map((x) => x.date);
}

const dollarsIn = (cents: number | null | undefined) =>
  cents === null || cents === undefined ? '' : (cents / 100).toFixed(2);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const audienceName = (d: Registration, k: string | null) =>
  k ? (d.audiences.find((a) => a.key === k)?.name ?? 'removed audience') : null;
const sentence = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const andList = (items: string[]) =>
  items.length < 2 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
const DASH = <span className="text-ink-3">–</span>;
const FORMAT: Record<string, string> = { in_person: 'In person', virtual: 'Virtual' };
const STATUS: Record<string, string> = { no_show: 'No show' };

/** Switch the event page to another tab by its name (the tab bar is the shell's; panels have no `go`). */
function openTab(label: string) {
  const tab = Array.from(document.querySelectorAll<HTMLButtonElement>('nav[aria-label="Event sections"] button')).find(
    (b) => b.textContent?.trim().startsWith(label),
  );
  if (!tab) return false;
  tab.click();
  window.scrollTo({ top: 0 });
  return true;
}

// ---------------------------------------------------------------- shared pieces

/** A table that becomes one block per row below 768px; each cell shows its column name there. */
const stackTable = 'w-full border-collapse tabular max-md:block';
const stackHead = 'max-md:hidden';
const stackBody = 'max-md:block';
const stackRow = 'border-b border-line hover:bg-selected max-md:block max-md:py-2';
const stackCell = `${td} max-md:block max-md:py-[3px] max-md:pr-0 max-md:text-left`;

function Cell({ label, className = '', children }: { label?: string; className?: string; children: ReactNode }) {
  return (
    <td data-label={label} className={`${stackCell} ${className}`}>
      {label ? <span className="md:hidden text-small text-ink-3">{label} · </span> : null}
      {children}
    </td>
  );
}

/** The row's actions: right-aligned on a laptop, a wrapping row at the end of the block on a phone. */
function ActionCell({ children }: { children: ReactNode }) {
  return (
    <td className={`${td} text-right whitespace-nowrap max-md:block max-md:text-left max-md:pt-2 max-md:pr-0`}>
      <div className="inline-flex flex-wrap items-center gap-2 justify-end max-md:justify-start">{children}</div>
    </td>
  );
}

/** A labelled field: label above, control, hint below. */
function Field({
  title,
  hint,
  children,
  wide = false,
}: {
  title: string;
  hint?: ReactNode;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <label className={`flex flex-col gap-[6px] min-w-0 ${wide ? 'col-span-full' : ''}`}>
      <span className="text-[15px] leading-5 font-semibold text-ink">{title}</span>
      {children}
      {hint ? <span className="text-small text-ink-3">{hint}</span> : null}
    </label>
  );
}

/** A group of checkboxes with a visible legend. */
function Checks({ legend, hint, children }: { legend: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <fieldset className="col-span-full min-w-0">
      <legend className="text-[15px] leading-5 font-semibold text-ink mb-2">{legend}</legend>
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-[15px] leading-[22px] text-ink">{children}</div>
      {hint ? <p className="text-small text-ink-3 mt-[6px]">{hint}</p> : null}
    </fieldset>
  );
}

function Check({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className="inline-flex items-center gap-2 min-h-[32px]">
      <input
        type="checkbox"
        className="w-[18px] h-[18px] accent-[#1D5C63]"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {children}
    </label>
  );
}

const grid2 = 'grid grid-cols-2 gap-x-6 gap-y-4 max-md:grid-cols-1';
const grid3 = 'grid grid-cols-3 gap-x-6 gap-y-4 max-md:grid-cols-1';
const subhead = 'font-serif font-semibold text-[17px] leading-6 text-ink mt-8 mb-2';

/** The dialog's footer: the action first, then Cancel, and what stops it or went wrong. */
function Footer({
  label,
  busy,
  error,
  missing,
  onSave,
  onCancel,
}: {
  label: string;
  busy: boolean;
  error: unknown;
  missing?: string | null;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      <Button variant="primary" disabled={busy || Boolean(missing)} onClick={onSave}>
        {label}
      </Button>
      <Button onClick={onCancel}>Cancel</Button>
      {missing ? <span className="text-small text-ink-3">{missing}</span> : null}
      {error ? (
        <div className="basis-full">
          <ErrorNotice>{errorMessage(error, (error as Error).message || 'Could not save.')}</ErrorNotice>
        </div>
      ) : null}
    </>
  );
}

function Shell({
  title,
  note,
  eventId,
  children,
  action,
}: {
  title: string;
  note?: string;
  eventId: string;
  children: (d: Registration) => ReactNode;
  action?: (d: Registration) => ReactNode;
}) {
  const q = useRegistration(eventId);
  return (
    <Panel>
      <PanelHeader title={title} note={note}>
        {q.data && action ? action(q.data) : null}
      </PanelHeader>
      <PanelBody>
        {q.isLoading ? (
          <SkeletonRows rows={3} />
        ) : q.error || !q.data ? (
          <ErrorNotice>{errorMessage(q.error, 'Could not load registration.')}</ErrorNotice>
        ) : (
          children(q.data)
        )}
      </PanelBody>
    </Panel>
  );
}

// ---------------------------------------------------------------- registrant progress (A12)

const NEXT_STEP: Record<string, string> = {
  registration: 'Register as an Associate Fellow',
  ticket: 'Get an Eventbrite ticket',
  pheedloop: 'Add to PheedLoop',
};

const STUCK_LEAD: Record<string, (n: number) => string> = {
  registration: (n) => `${n} still need${n === 1 ? 's' : ''} to register as an Associate Fellow`,
  ticket: (n) => `${n} still need${n === 1 ? 's' : ''} an Eventbrite ticket`,
  pheedloop: (n) => `${n} ${n === 1 ? "isn't" : "aren't"} in PheedLoop yet`,
};

function ProgressLead({ d, canEdit }: { d: Registration; canEdit: boolean }) {
  const p = d.progress;
  const stuck = p.steps.filter((s) => s.stuckHere);
  const lead = !p.total
    ? 'Nobody has registered publicly yet.'
    : `${plural(p.total, 'person', 'people')} registered publicly. ${
        stuck.length
          ? `${andList(stuck.map((s) => (STUCK_LEAD[s.key] ?? ((n: number) => `${n} are stuck at ${s.label}`))(s.stuckHere)))}.`
          : `${p.total === 1 ? 'They have' : `All ${p.total} have`} finished all three steps.`
      }`;
  const toPheedloop = p.steps.find((s) => s.key === 'pheedloop')?.stuckHere ?? 0;
  return (
    <Lead
      lead={lead}
      sub="Public registrants go through three steps: Associate Fellow registration, an Eventbrite ticket, then PheedLoop. This page tracks them and the tools that bring people in."
    >
      {canEdit && toPheedloop ? (
        <Button variant="primary" onClick={() => openTab('Documents')}>
          Add the {toPheedloop} to PheedLoop
        </Button>
      ) : null}
    </Lead>
  );
}

/** Each public registrant's three steps and who is stuck where. Opens the tab with its lead sentence. */
export function RegistrantProgressPanel({ eventId, canEdit }: SimplePanelProps) {
  const [stuck, setStuck] = useState<string>('any');
  const q = useRegistration(eventId);
  return (
    <>
      {q.data ? (
        <div className="mb-12 max-md:mb-8">
          <ProgressLead d={q.data} canEdit={canEdit} />
        </div>
      ) : null}
      <Shell title="Public registration steps" eventId={eventId}>
        {(d) => {
          const p = d.progress;
          const all = p.people ?? [];
          const count = (k: string) =>
            all.filter((x: Registrant) => (k === 'any' ? x.stuckAt : k === 'done' ? !x.stuckAt : x.stuckAt === k))
              .length;
          const people = all.filter((x: Registrant) =>
            stuck === 'any' ? x.stuckAt : stuck === 'done' ? !x.stuckAt : x.stuckAt === stuck,
          );
          const show = (k: string) => {
            setStuck(k);
            document.getElementById(`reg-show-${eventId}`)?.focus();
          };
          return (
            <>
              <div className="overflow-x-auto">
                <table className="w-full max-w-[720px] border-collapse tabular">
                  <thead>
                    <tr className={headRow}>
                      <th className={th}>Step</th>
                      <th className={`${th} ${num}`}>Done</th>
                      <th className={`${th} ${num}`}>Stuck here</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.steps.map((s, i) => (
                      <tr key={s.key} className="border-b border-line">
                        <td className={td}>
                          {i + 1}. {s.label}
                        </td>
                        <td className={`${td} ${num}`}>
                          {s.done} of {p.total}
                        </td>
                        <td className={`${td} ${num}`}>
                          {!s.stuckHere ? (
                            DASH
                          ) : d.visible ? (
                            <button
                              type="button"
                              className="text-accent underline underline-offset-[3px] hover:text-ink"
                              aria-label={`Show the ${s.stuckHere} stuck at ${s.label}`}
                              onClick={() => show(s.key)}
                            >
                              {s.stuckHere}
                            </button>
                          ) : (
                            s.stuckHere
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-line-strong font-semibold">
                      <td className={`${td} font-semibold`}>All three steps</td>
                      <td className={`${td} ${num} font-semibold`}>
                        {p.complete} of {p.total}
                      </td>
                      <td className={td} />
                    </tr>
                  </tfoot>
                </table>
              </div>
              {!d.visible ? (
                <div className="mt-6">
                  <InfoNotice>Names are shown to admins.</InfoNotice>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-3 mt-8 mb-2">
                    <label htmlFor={`reg-show-${eventId}`} className="text-[15px] leading-5 font-semibold text-ink">
                      Show
                    </label>
                    <select
                      id={`reg-show-${eventId}`}
                      className={`${inputClass} !w-auto min-h-[40px] py-2`}
                      value={stuck}
                      onChange={(e) => setStuck(e.target.value)}
                    >
                      <option value="any">Everyone stuck ({count('any')})</option>
                      {p.steps.map((s, i) => (
                        <option key={s.key} value={s.key}>
                          Stuck at step {i + 1}, {s.label} ({count(s.key)})
                        </option>
                      ))}
                      <option value="done">All three done ({count('done')})</option>
                    </select>
                  </div>
                  {people.length ? (
                    <table className={stackTable}>
                      <thead className={stackHead}>
                        <tr className={headRow}>
                          <th className={th}>Person</th>
                          <th className={th}>Status</th>
                          <th className={th}>Next step</th>
                        </tr>
                      </thead>
                      <tbody className={stackBody}>
                        {people.map((x) => (
                          <tr key={x.inviteeId} className={stackRow}>
                            <Cell>
                              <span className="font-semibold">{x.name}</span>
                              <span className={em}>{x.email ?? 'No email'}</span>
                            </Cell>
                            <Cell label="Status">{STATUS[x.status] ?? sentence(x.status)}</Cell>
                            <Cell label="Next step">{x.stuckAt ? NEXT_STEP[x.stuckAt] : 'Done'}</Cell>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-[15px] leading-[22px] text-ink-2 py-3">Nobody here.</p>
                  )}
                </>
              )}
            </>
          );
        }}
      </Shell>
    </>
  );
}

// ---------------------------------------------------------------- tickets (B2)

type TicketRow = {
  key?: string;
  name: string;
  price: string;
  capacity: string;
  format: string;
  audienceKey: string;
  opensOn: string;
  closesOn: string;
};

function TicketsDialog({ eventId, d, onDone }: { eventId: string; d: Registration; onDone: () => void }) {
  const t = d.tickets;
  const [form, setForm] = useState({
    salesOpen: t.salesOpen ?? '',
    salesClose: t.salesClose ?? '',
    eventbriteUrl: t.eventbriteUrl ?? '',
  });
  const [rows, setRows] = useState<TicketRow[]>(
    t.types.map((x) => ({
      key: x.key,
      name: x.name,
      price: dollarsIn(x.priceCents),
      capacity: x.capacity == null ? '' : String(x.capacity),
      format: x.format ?? '',
      audienceKey: x.audienceKey ?? '',
      opensOn: x.opensOn ?? '',
      closesOn: x.closesOn ?? '',
    })),
  );
  const [more, setMore] = useState<number[]>([]);
  const refresh = useRefresh(eventId);
  const save = useMutation(
    async () => {
      const types = rows.map((r) => {
        const price = toCents(r.price);
        if (Number.isNaN(price)) throw new Error(`${r.name}: enter a price like 75.00`);
        return {
          key: r.key,
          name: r.name,
          priceCents: price,
          capacity: r.capacity === '' ? null : Number(r.capacity),
          format: r.format || null,
          audienceKey: r.audienceKey || null,
          opensOn: r.opensOn || null,
          closesOn: r.closesOn || null,
        };
      });
      return api.put(`/events/${eventId}/registration/tickets`, {
        salesOpen: form.salesOpen || null,
        salesClose: form.salesClose || null,
        eventbriteUrl: form.eventbriteUrl || null,
        types,
      });
    },
    {
      onSuccess: () => {
        refresh();
        onDone();
      },
    },
  );
  const set = (i: number, k: keyof TicketRow, v: string) =>
    setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const toggle = (i: number) => setMore(more.includes(i) ? more.filter((x) => x !== i) : [...more, i]);
  const unnamed = rows.some((r) => !r.name.trim());
  const cell = 'py-2 pr-2 last:pr-0 align-top max-md:block max-md:pr-0 max-md:py-1';
  const phoneLabel = (s: string) => <span className="md:hidden block text-small text-ink-3 mb-1">{s}</span>;
  return (
    <Modal
      title="Edit tickets"
      why="The Treasury request on Budget & vendors uses these same tickets."
      onClose={onDone}
      width={780}
      footer={
        <Footer
          label="Save tickets"
          busy={save.isLoading}
          error={save.error}
          missing={unnamed ? 'Give every ticket a name.' : null}
          onSave={() => save.mutate()}
          onCancel={onDone}
        />
      }
    >
      <div className={grid3}>
        <Field title="Sales open">
          <input
            type="date"
            className={inputClass}
            value={form.salesOpen}
            onChange={(e) => setForm({ ...form, salesOpen: e.target.value })}
          />
        </Field>
        <Field title="Sales close">
          <input
            type="date"
            className={inputClass}
            value={form.salesClose}
            onChange={(e) => setForm({ ...form, salesClose: e.target.value })}
          />
        </Field>
        <Field title="Eventbrite page" hint="https://…">
          <input
            type="url"
            className={inputClass}
            value={form.eventbriteUrl}
            onChange={(e) => setForm({ ...form, eventbriteUrl: e.target.value })}
          />
        </Field>
      </div>
      {rows.length ? (
        <table className="w-full border-collapse mt-5 max-md:block">
          <thead className="max-md:hidden">
            <tr className={headRow}>
              <th className={th}>Ticket</th>
              <th className={`${th} w-[140px]`}>Format</th>
              <th className={`${th} ${num} w-[110px]`}>Price</th>
              <th className={`${th} ${num} w-[110px]`}>Capacity</th>
              <th className={`${th} w-[84px]`}>
                <span className="sr-only">More and remove</span>
              </th>
            </tr>
          </thead>
          <tbody className="max-md:block">
            {rows.map((r, i) => {
              const open = more.includes(i);
              const name = r.name || `ticket ${i + 1}`;
              return [
                <tr
                  key={`${r.key ?? i}-row`}
                  className={`max-md:block max-md:pt-3 ${open ? '' : 'border-b border-line'}`}
                >
                  <td className={cell}>
                    {phoneLabel('Ticket')}
                    <input
                      className={inputClass}
                      aria-label="Ticket name"
                      value={r.name}
                      onChange={(e) => set(i, 'name', e.target.value)}
                    />
                  </td>
                  <td className={cell}>
                    {phoneLabel('Format')}
                    <select
                      className={inputClass}
                      aria-label={`Format of ${name}`}
                      value={r.format}
                      onChange={(e) => set(i, 'format', e.target.value)}
                    >
                      <option value="">–</option>
                      <option value="in_person">In person</option>
                      <option value="virtual">Virtual</option>
                    </select>
                  </td>
                  <td className={cell}>
                    {phoneLabel('Price ($)')}
                    <input
                      className={`${inputClass} text-right tabular`}
                      aria-label={`Price of ${name} in dollars`}
                      placeholder="$"
                      value={r.price}
                      onChange={(e) => set(i, 'price', e.target.value)}
                      inputMode="decimal"
                    />
                  </td>
                  <td className={cell}>
                    {phoneLabel('Capacity')}
                    <input
                      className={`${inputClass} text-right tabular`}
                      aria-label={`Capacity of ${name}`}
                      placeholder="No limit"
                      value={r.capacity}
                      onChange={(e) => set(i, 'capacity', e.target.value)}
                      inputMode="numeric"
                    />
                  </td>
                  <td className={`${cell} whitespace-nowrap`}>
                    <div className="flex gap-1 md:mt-1 max-md:mt-1">
                      <Button
                        size="sm"
                        variant="icon"
                        aria-expanded={open}
                        aria-label={`More for ${name}: audience and sales dates`}
                        onClick={() => toggle(i)}
                      >
                        ⋯
                      </Button>
                      <Button
                        size="sm"
                        variant="icon"
                        aria-label={`Remove ${name}`}
                        onClick={() => {
                          setRows(rows.filter((_, j) => j !== i));
                          setMore(more.filter((x) => x !== i).map((x) => (x > i ? x - 1 : x)));
                        }}
                      >
                        ×
                      </Button>
                    </div>
                  </td>
                </tr>,
                open ? (
                  <tr key={`${r.key ?? i}-more`} className="border-b border-line max-md:block">
                    <td colSpan={5} className="pb-3 pt-1 max-md:block">
                      <div className={grid3}>
                        <Field title="Audience">
                          <select
                            className={inputClass}
                            value={r.audienceKey}
                            onChange={(e) => set(i, 'audienceKey', e.target.value)}
                          >
                            <option value="">Anyone</option>
                            {d.audiences.map((a) => (
                              <option key={a.key} value={a.key}>
                                {a.name}
                              </option>
                            ))}
                          </select>
                        </Field>
                        <Field title="Opens" hint="If different from the sales dates">
                          <input
                            type="date"
                            className={inputClass}
                            value={r.opensOn}
                            onChange={(e) => set(i, 'opensOn', e.target.value)}
                          />
                        </Field>
                        <Field title="Closes" hint="If different from the sales dates">
                          <input
                            type="date"
                            className={inputClass}
                            value={r.closesOn}
                            onChange={(e) => set(i, 'closesOn', e.target.value)}
                          />
                        </Field>
                      </div>
                    </td>
                  </tr>
                ) : null,
              ];
            })}
          </tbody>
        </table>
      ) : (
        <p className="mt-5 text-ink-2">No ticket types yet.</p>
      )}
      <p className="mt-3 text-[15px] leading-[22px]">
        <button
          type="button"
          className="text-accent underline underline-offset-[3px] hover:text-ink"
          onClick={() =>
            setRows([
              ...rows,
              { name: 'Ticket', price: '', capacity: '', format: '', audienceKey: '', opensOn: '', closesOn: '' },
            ])
          }
        >
          Add a ticket type
        </button>
        <span className="text-ink-3"> · Audience and per-ticket sales dates are under each ticket&apos;s “⋯”.</span>
      </p>
    </Modal>
  );
}

function TicketTypesTable({ d }: { d: Registration }) {
  return (
    <table className={stackTable}>
      <thead className={stackHead}>
        <tr className={headRow}>
          <th className={th}>Ticket</th>
          <th className={`${th} ${num}`}>Price</th>
          <th className={`${th} ${num}`}>Capacity</th>
          <th className={`${th} ${num}`}>Sold</th>
          <th className={`${th} ${num}`}>Comp</th>
          <th className={`${th} ${num}`}>Left</th>
          <th className={th}>Sales window</th>
        </tr>
      </thead>
      <tbody className={stackBody}>
        {d.tickets.types.map((t: TicketType) => {
          const about = [t.format ? FORMAT[t.format] : null, audienceName(d, t.audienceKey)]
            .filter(Boolean)
            .join(' · ');
          return (
            <tr key={t.key} className={stackRow}>
              <Cell>
                <span className="font-semibold">{t.name}</span>
                {about ? <span className={em}>{about}</span> : null}
              </Cell>
              <Cell label="Price" className={num}>
                {t.priceCents == null ? DASH : money(t.priceCents)}
              </Cell>
              <Cell label="Capacity" className={num}>
                {t.capacity ?? 'No limit'}
              </Cell>
              <Cell label="Sold" className={num}>
                {t.sold}
              </Cell>
              <Cell label="Comp" className={num}>
                {t.comps || DASH}
              </Cell>
              <Cell label="Left" className={num}>
                {t.full ? (
                  <span className="inline-flex">
                    <Pill tone="problem" small>
                      Full
                    </Pill>
                  </span>
                ) : (
                  (t.left ?? DASH)
                )}
              </Cell>
              <Cell label="Sales window">
                {sentence(WINDOW_LABEL[t.window])}
                {t.opensOn || t.closesOn ? (
                  <span className={em}>
                    {dayText(t.opensOn)} to {dayText(t.closesOn)}
                  </span>
                ) : null}
              </Cell>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Eventbrite ticket types, capacities and sales windows; the Treasury request reads the same ones. */
export function TicketsPanel({ eventId, canEdit }: SimplePanelProps) {
  const [editing, setEditing] = useState(false);
  return (
    <Shell
      title="Tickets"
      eventId={eventId}
      note="Eventbrite"
      action={(d) =>
        canEdit && d.visible && !(d.tickets.window === 'closed' && !d.tickets.types.length) ? (
          <Button size="sm" onClick={() => setEditing(true)}>
            Edit tickets
          </Button>
        ) : null
      }
    >
      {(d) => {
        const t = d.tickets;
        const over = t.window === 'closed' && !t.types.length;
        const window = t.datesSet ? sentence(WINDOW_LABEL[t.window]) : 'Not set';
        return (
          <>
            {editing && d.visible ? <TicketsDialog eventId={eventId} d={d} onDone={() => setEditing(false)} /> : null}
            <KpiBand
              cells={[
                {
                  label: t.totals.capacity ? `sold of ${t.totals.capacity}` : 'sold',
                  value: t.totals.sold,
                },
                ...(t.totals.comps ? [{ label: 'complimentary', value: t.totals.comps }] : []),
                {
                  label: t.datesSet ? `sales ${dayText(t.salesOpen)} to ${dayText(t.salesClose)}` : 'sales dates',
                  value: window,
                },
              ]}
            />
            {t.eventbriteUrl ? (
              <p className="text-[15px] leading-[22px] mb-3">
                <a href={t.eventbriteUrl} target="_blank" rel="noreferrer">
                  Eventbrite page
                </a>
              </p>
            ) : null}
            {t.types.length ? <TicketTypesTable d={d} /> : null}
            {!t.types.length && !t.unmatched.length && !over ? (
              <p className="text-[15px] leading-[22px] text-ink-2">
                No ticket types yet.
                {canEdit
                  ? ' Add them in Edit tickets; the Treasury request on Budget & vendors uses the same ones.'
                  : ''}
              </p>
            ) : null}
            {t.unmatched.length ? (
              <>
                {!t.types.length && !over ? (
                  <p className="text-[15px] leading-[22px] text-ink-2 mb-3 max-w-[72ch]">
                    No ticket types are set up here yet, but{' '}
                    {t.unmatched.length === 1 ? 'one is' : `${t.unmatched.length} are`} selling on Eventbrite.
                    {canEdit
                      ? ' Add them in Edit tickets so the Treasury request on Budget & vendors uses the same ones.'
                      : ''}
                  </p>
                ) : null}
                <table className={`${stackTable} max-w-[720px] ${t.types.length ? 'mt-6' : ''}`}>
                  <thead className={stackHead}>
                    <tr className={headRow}>
                      <th className={th}>
                        {over
                          ? 'Sold by ticket type'
                          : t.types.length
                            ? 'On Eventbrite, not a ticket type here'
                            : 'Selling on Eventbrite'}
                      </th>
                      <th className={`${th} ${num}`}>Sold</th>
                    </tr>
                  </thead>
                  <tbody className={stackBody}>
                    {t.unmatched.map((u) => (
                      <tr key={u.name} className={stackRow}>
                        <Cell>{u.name}</Cell>
                        <Cell label="Sold" className={num}>
                          {u.sold}
                        </Cell>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : null}
            {over ? null : (
              <p className="text-small text-ink-3 mt-3">
                {t.soldFrom === 'sync'
                  ? 'Sold counts come from the live Eventbrite sync.'
                  : 'Sold counts come from the last Eventbrite export uploaded.'}
              </p>
            )}
          </>
        );
      }}
    </Shell>
  );
}

// ---------------------------------------------------------------- outreach lists and send log (A11)

function ListDialog({
  eventId,
  d,
  list,
  onDone,
}: {
  eventId: string;
  d: Registration;
  list?: OutreachList;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    name: list?.name ?? '',
    kinds: list?.kinds ?? [],
    tags: (list?.tags ?? []).join(', '),
    audienceKey: list?.audienceKey ?? d.audiences.find((a) => a.public)?.key ?? '',
    notes: list?.notes ?? '',
  });
  const refresh = useRefresh(eventId);
  const save = useMutation(
    async () => {
      const body = {
        name: form.name,
        kinds: form.kinds,
        tags: form.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        audienceKey: form.audienceKey || null,
        notes: form.notes || null,
      };
      return list
        ? api.patch(`/events/${eventId}/registration/lists/${list.id}`, body)
        : api.post(`/events/${eventId}/registration/lists`, body);
    },
    {
      onSuccess: () => {
        refresh();
        onDone();
      },
    },
  );
  return (
    <Modal
      title={list ? `Edit “${list.name}”` : 'New outreach list'}
      why="A list picks contacts by kind and tags, for an email you send from your own account."
      onClose={onDone}
      width={640}
      footer={
        <Footer
          label={list ? 'Save' : 'Make the list'}
          busy={save.isLoading}
          error={save.error}
          missing={form.name.trim() ? null : 'Give the list a name.'}
          onSave={() => save.mutate()}
          onCancel={onDone}
        />
      }
    >
      <div className={grid2}>
        <Field title="List name">
          <input
            className={inputClass}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Psychology department heads, Ontario"
          />
        </Field>
        <Field title="For the audience">
          <select
            className={inputClass}
            value={form.audienceKey}
            onChange={(e) => setForm({ ...form, audienceKey: e.target.value })}
          >
            <option value="">–</option>
            {d.audiences.map((a) => (
              <option key={a.key} value={a.key}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>
        <Checks legend="Contacts of any of these kinds">
          {d.labels.outreachKinds.map((k) => (
            <Check
              key={k}
              checked={form.kinds.includes(k)}
              onChange={(on) =>
                setForm({ ...form, kinds: on ? [...form.kinds, k] : form.kinds.filter((x) => x !== k) })
              }
            >
              {KIND_LABEL[k] ?? k}
            </Check>
          ))}
        </Checks>
        <Field
          title="With every one of these tags"
          hint="Comma separated: discipline, province… Tags are set on each contact on the Contacts page."
          wide
        >
          <input
            className={inputClass}
            value={form.tags}
            onChange={(e) => setForm({ ...form, tags: e.target.value })}
            placeholder="psychology, on"
          />
        </Field>
        <Field title="Notes" wide>
          <input
            className={inputClass}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </Field>
      </div>
    </Modal>
  );
}

function PrepareDialog({ eventId, list, onDone }: { eventId: string; list: OutreachList; onDone: () => void }) {
  const [who, setWho] = useState({ name: '', email: '' });
  const [msg, setMsg] = useState<PreparedOutreach | null>(null);
  const refresh = useRefresh(eventId);
  const prepare = useMutation(
    async () =>
      (
        await api.get(`/events/${eventId}/registration/lists/${list.id}/message`, {
          params: { onBehalfOfName: who.name || undefined, onBehalfOfEmail: who.email || undefined },
        })
      ).data.data as PreparedOutreach,
    { onSuccess: setMsg },
  );
  const sent = useMutation(
    async () =>
      api.post(`/events/${eventId}/registration/sends`, {
        kind: 'outreach',
        listId: list.id,
        audienceKey: list.audienceKey,
        recipients: msg?.recipients ?? 0,
        subject: msg?.subject,
        onBehalfOf: who.name || who.email ? { name: who.name || null, email: who.email || null } : null,
      }),
    {
      onSuccess: () => {
        refresh();
        onDone();
      },
    },
  );
  const error = prepare.error || sent.error;
  const select = (e: { target: HTMLInputElement | HTMLTextAreaElement }) => e.target.select();
  return (
    <Modal
      title={`Email to “${list.name}”`}
      why="The platform prepares the email; copy it into your own email and send it. It does not send it."
      onClose={onDone}
      width={720}
      footer={
        msg ? (
          <>
            <Button variant="primary" disabled={sent.isLoading} onClick={() => sent.mutate()}>
              I sent it
            </Button>
            <Button onClick={onDone}>Close</Button>
          </>
        ) : (
          <>
            <Button variant="primary" disabled={prepare.isLoading} onClick={() => prepare.mutate()}>
              Prepare the email
            </Button>
            <Button onClick={onDone}>Cancel</Button>
          </>
        )
      }
    >
      <div className={grid2}>
        <Field title="On behalf of" hint="An ambassador, optional">
          <input className={inputClass} value={who.name} onChange={(e) => setWho({ ...who, name: e.target.value })} />
        </Field>
        <Field title="Their email">
          <input
            type="email"
            className={inputClass}
            value={who.email}
            onChange={(e) => setWho({ ...who, email: e.target.value })}
          />
        </Field>
      </div>
      {msg ? (
        <div className="mt-6 grid gap-4">
          <p className="text-ink">
            {plural(msg.recipients, 'recipient')} in Bcc
            {msg.unsubscribed ? `, ${msg.unsubscribed} unsubscribed left out` : ''}
            {msg.withoutEmail ? `, ${msg.withoutEmail} without an email` : ''}.
          </p>
          <Field title="Bcc">
            <textarea readOnly onFocus={select} className={`${inputClass} h-20`} value={msg.bcc.join('; ')} />
          </Field>
          <Field title="Subject">
            <input readOnly onFocus={select} className={inputClass} value={msg.subject} />
          </Field>
          <Field title="Message">
            <textarea readOnly onFocus={select} className={`${inputClass} h-56`} value={msg.text} />
          </Field>
          {prepare.isLoading ? null : (
            <p>
              <button
                type="button"
                className="text-accent underline underline-offset-[3px] hover:text-ink text-[15px]"
                onClick={() => prepare.mutate()}
              >
                Prepare again
              </button>
              <span className="text-small text-ink-3"> after changing who it is on behalf of</span>
            </p>
          )}
        </div>
      ) : null}
      {error ? (
        <div className="mt-4">
          <ErrorNotice>{errorMessage(error, 'Could not prepare the email.')}</ErrorNotice>
        </div>
      ) : null}
    </Modal>
  );
}

function WhoDialog({ eventId, list, onDone }: { eventId: string; list: OutreachList; onDone: () => void }) {
  const q = useQuery(
    ['event-registration-list', eventId, list.id],
    async () =>
      (await api.get(`/events/${eventId}/registration/lists/${list.id}`)).data.data as {
        contacts: { id: string; name: string; email: string | null; organization: string | null; tags: string[] }[];
      },
  );
  return (
    <Modal
      title={`${list.name} · ${plural(list.count, 'contact')}`}
      why="The contacts this list picks today. Change kinds or tags with Edit, or a contact's tags on the Contacts page."
      onClose={onDone}
      width={720}
      footer={<Button onClick={onDone}>Close</Button>}
    >
      {q.isLoading ? (
        <SkeletonRows rows={3} />
      ) : q.error || !q.data ? (
        <ErrorNotice>{errorMessage(q.error, 'Could not load the list.')}</ErrorNotice>
      ) : q.data.contacts.length ? (
        <div className="max-h-[50vh] overflow-y-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className={headRow}>
                <th className={th}>Contact</th>
                <th className={th}>Organization</th>
              </tr>
            </thead>
            <tbody>
              {q.data.contacts.map((c) => (
                <tr key={c.id} className="border-b border-line">
                  <td className={td}>
                    {c.name}
                    <span className={em}>{c.email ?? 'No email'}</span>
                  </td>
                  <td className={td}>{c.organization ?? DASH}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-ink-2">No contacts match this list.</p>
      )}
    </Modal>
  );
}

function SendDialog({ eventId, d, onDone }: { eventId: string; d: Registration; onDone: () => void }) {
  const [form, setForm] = useState({
    kind: 'outreach',
    sentOn: new Date().toISOString().slice(0, 10),
    audienceKey: '',
    listId: '',
    recipients: '',
    subject: '',
    note: '',
    name: '',
    email: '',
  });
  const refresh = useRefresh(eventId);
  const save = useMutation(
    async () =>
      api.post(`/events/${eventId}/registration/sends`, {
        kind: form.kind,
        sentOn: form.sentOn,
        audienceKey: form.audienceKey || null,
        listId: form.listId || null,
        recipients: form.recipients === '' ? 0 : Number(form.recipients),
        subject: form.subject || null,
        note: form.note || null,
        onBehalfOf: form.name || form.email ? { name: form.name || null, email: form.email || null } : null,
      }),
    {
      onSuccess: () => {
        refresh();
        onDone();
      },
    },
  );
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  return (
    <Modal
      title="Log a send"
      why="Record an email that went out from your own account, so the per-audience counts stay right."
      onClose={onDone}
      width={640}
      footer={
        <Footer
          label="Log it"
          busy={save.isLoading}
          error={save.error}
          onSave={() => save.mutate()}
          onCancel={onDone}
        />
      }
    >
      <div className={grid2}>
        <Field title="What">
          <select className={inputClass} value={form.kind} onChange={set('kind')}>
            {Object.entries(d.labels.sendKinds).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Sent on">
          <input type="date" className={inputClass} value={form.sentOn} onChange={set('sentOn')} />
        </Field>
        <Field title="Audience">
          <select className={inputClass} value={form.audienceKey} onChange={set('audienceKey')}>
            <option value="">–</option>
            {d.audiences.map((a) => (
              <option key={a.key} value={a.key}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>
        <Field title="List">
          <select className={inputClass} value={form.listId} onChange={set('listId')}>
            <option value="">–</option>
            {d.lists.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Recipients">
          <input
            className={`${inputClass} text-right tabular`}
            value={form.recipients}
            onChange={set('recipients')}
            inputMode="numeric"
          />
        </Field>
        <Field title="Subject">
          <input className={inputClass} value={form.subject} onChange={set('subject')} />
        </Field>
        <Field title="On behalf of">
          <input className={inputClass} value={form.name} onChange={set('name')} />
        </Field>
        <Field title="Their email">
          <input type="email" className={inputClass} value={form.email} onChange={set('email')} />
        </Field>
        <Field title="Note" wide>
          <input className={inputClass} value={form.note} onChange={set('note')} />
        </Field>
      </div>
    </Modal>
  );
}

function ListsTable({
  eventId,
  d,
  canEdit,
  open,
}: {
  eventId: string;
  d: Registration;
  canEdit: boolean;
  open: (mode: 'who' | 'email' | 'edit', list: OutreachList) => void;
}) {
  const refresh = useRefresh(eventId);
  const remove = useMutation(async (id: string) => api.delete(`/events/${eventId}/registration/lists/${id}`), {
    onSuccess: refresh,
  });
  return (
    <table className={stackTable}>
      <thead className={stackHead}>
        <tr className={headRow}>
          <th className={th}>List</th>
          <th className={`${th} ${num}`}>Contacts</th>
          <th className={th}>Who it targets</th>
          {d.visible ? (
            <th className={th}>
              <span className="sr-only">Actions</span>
            </th>
          ) : null}
        </tr>
      </thead>
      <tbody className={stackBody}>
        {d.lists.map((l) => {
          const targets = [
            l.kinds.map((k) => KIND_LABEL[k] ?? k).join(' or '),
            l.tags.length ? `tags: ${l.tags.join(', ')}` : null,
            audienceName(d, l.audienceKey) ? `for ${audienceName(d, l.audienceKey)}` : null,
          ].filter(Boolean);
          return (
            <tr key={l.id} className={stackRow}>
              <Cell>
                <span className="font-semibold">{l.name}</span>
                {l.notes ? <span className={em}>{l.notes}</span> : null}
              </Cell>
              <Cell label="Contacts" className={num}>
                {l.count}
                {l.withEmail != null && l.withEmail < l.count ? (
                  <span className={em}>{l.withEmail} with email</span>
                ) : null}
              </Cell>
              <Cell label="Who it targets">{targets.length ? targets.join(' · ') : DASH}</Cell>
              {d.visible ? (
                <ActionCell>
                  {canEdit ? (
                    <Button size="sm" onClick={() => open('email', l)}>
                      Prepare email
                    </Button>
                  ) : null}
                  <MoreMenu
                    label={`More for ${l.name}`}
                    items={[
                      { label: 'Who is on it', onClick: () => open('who', l) },
                      ...(canEdit
                        ? [
                            { label: 'Edit', onClick: () => open('edit', l) },
                            {
                              label: 'Remove',
                              destructive: true,
                              disabled: remove.isLoading,
                              onClick: () =>
                                void confirmAction(
                                  `Remove the list "${l.name}"? Its contacts stay.`,
                                  'Remove the list',
                                ).then((ok) => ok && remove.mutate(l.id)),
                            },
                          ]
                        : []),
                    ]}
                  />
                </ActionCell>
              ) : null}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function SendLog({ eventId, d, canEdit }: { eventId: string; d: Registration; canEdit: boolean }) {
  const refresh = useRefresh(eventId);
  const remove = useMutation(async (id: string) => api.delete(`/events/${eventId}/registration/sends/${id}`), {
    onSuccess: refresh,
  });
  const log = d.sendLog ?? [];
  if (!log.length) return <p className="text-[15px] leading-[22px] text-ink-2">Nothing logged yet.</p>;
  return (
    <table className={stackTable}>
      <thead className={stackHead}>
        <tr className={headRow}>
          <th className={th}>Sent</th>
          <th className={th}>What</th>
          <th className={th}>To</th>
          <th className={th}>On behalf of</th>
          <th className={`${th} ${num}`}>Recipients</th>
          <th className={th}>
            <span className="sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      <tbody className={stackBody}>
        {log.map((s: SendEntry) => {
          const to = [audienceName(d, s.audienceKey), s.listId ? d.lists.find((l) => l.id === s.listId)?.name : null]
            .filter(Boolean)
            .join(' · ');
          const what = d.labels.sendKinds[s.kind] ?? s.kind;
          return (
            <tr key={s.id} className={stackRow}>
              <Cell>
                <span className="whitespace-nowrap">{dayText(s.sentOn)}</span>
              </Cell>
              <Cell label="What">
                {what}
                {s.subject ? <span className={em}>{s.subject}</span> : null}
                {s.note ? <span className={em}>{s.note}</span> : null}
                {s.automatic ? <span className={em}>From the Emails tab</span> : null}
              </Cell>
              <Cell label="To">{to || DASH}</Cell>
              <Cell label="On behalf of">
                {s.onBehalfOf ? [s.onBehalfOf.name, s.onBehalfOf.email].filter(Boolean).join(', ') : DASH}
              </Cell>
              <Cell label="Recipients" className={num}>
                {s.recipients}
              </Cell>
              <ActionCell>
                {canEdit && !s.automatic ? (
                  <MoreMenu
                    label={`More for the ${what.toLowerCase()} sent ${dayText(s.sentOn)}`}
                    items={[
                      {
                        label: 'Remove from the log',
                        destructive: true,
                        disabled: remove.isLoading,
                        onClick: () =>
                          void confirmAction('Remove this entry from the send log?', 'Remove the entry').then(
                            (ok) => ok && remove.mutate(s.id),
                          ),
                      },
                    ]}
                  />
                ) : null}
              </ActionCell>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

type OutreachDialog =
  { kind: 'list' } | { kind: 'send' } | { kind: 'who' | 'email' | 'edit'; list: OutreachList } | null;

/** Outreach lists of contacts, prepared emails, and the log of what went out per audience. */
export function OutreachPanel({ eventId, canEdit }: SimplePanelProps) {
  const [dialog, setDialog] = useState<OutreachDialog>(null);
  const q = useRegistration(eventId);
  const close = () => setDialog(null);
  const dialogs = (d: Registration) =>
    dialog?.kind === 'list' ? (
      <ListDialog eventId={eventId} d={d} onDone={close} />
    ) : dialog?.kind === 'send' ? (
      <SendDialog eventId={eventId} d={d} onDone={close} />
    ) : dialog?.kind === 'edit' ? (
      <ListDialog eventId={eventId} d={d} list={dialog.list} onDone={close} />
    ) : dialog?.kind === 'email' ? (
      <PrepareDialog eventId={eventId} list={dialog.list} onDone={close} />
    ) : dialog?.kind === 'who' ? (
      <WhoDialog eventId={eventId} list={dialog.list} onDone={close} />
    ) : null;
  if (q.data && !q.data.lists.length && !q.data.sendsByAudience.some((a) => a.sends))
    return canEdit && q.data.visible ? (
      <>
        <AddLink label="Outreach list" onClick={() => setDialog({ kind: 'list' })} />
        <AddLink label="Log a send" onClick={() => setDialog({ kind: 'send' })} />
        {dialogs(q.data)}
      </>
    ) : null;
  return (
    <Shell
      title="Outreach"
      eventId={eventId}
      note="emails you send from your own account"
      action={(d) =>
        canEdit && d.visible ? (
          <>
            <Button size="sm" onClick={() => setDialog({ kind: 'list' })}>
              New list
            </Button>
            <Button size="sm" variant="quiet" onClick={() => setDialog({ kind: 'send' })}>
              Log a send
            </Button>
          </>
        ) : null
      }
    >
      {(d) => (
        <>
          {dialogs(d)}
          <div className="overflow-x-auto">
            <table className="w-full max-w-[720px] border-collapse tabular">
              <thead>
                <tr className={headRow}>
                  <th className={th}>Audience</th>
                  <th className={`${th} ${num}`}>Sends</th>
                  <th className={`${th} ${num}`}>Recipients</th>
                  <th className={th}>Last</th>
                </tr>
              </thead>
              <tbody>
                {d.sendsByAudience.map((a) => (
                  <tr key={a.key} className="border-b border-line">
                    <td className={td}>{a.name}</td>
                    <td className={`${td} ${num}`}>{a.sends || DASH}</td>
                    <td className={`${td} ${num}`}>{a.recipients || DASH}</td>
                    <td className={td}>{a.lastSentOn ? dayText(a.lastSentOn) : 'Nothing sent'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {d.lists.length ? (
            <>
              <h3 className={subhead}>Lists</h3>
              <ListsTable eventId={eventId} d={d} canEdit={canEdit} open={(kind, list) => setDialog({ kind, list })} />
            </>
          ) : null}
          <h3 className={subhead}>Send log</h3>
          {d.visible ? (
            <SendLog eventId={eventId} d={d} canEdit={canEdit} />
          ) : (
            <InfoNotice>{plural(d.counts.sends, 'send')} logged. Details are shown to admins.</InfoNotice>
          )}
        </>
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------- calendar invites (A15)

/** A calendar invite (.ics, English and French) per audience, to attach or share. */
export function CalendarInvitesPanel({ eventId, canEdit }: SimplePanelProps) {
  const refresh = useRefresh(eventId);
  const eventDays = useEventDays(eventId);
  const get = useMutation(
    async (audience: string) =>
      (await api.get(`/events/${eventId}/registration/calendar`, { params: { audience } })).data.data as {
        fileName: string;
        contentType: string;
        ics: string;
      },
    {
      onSuccess: (f) => downloadText(f.fileName, f.contentType, f.ics),
    },
  );
  const mark = useMutation(
    async ({ audience, recipients }: { audience: string; recipients: number }) =>
      api.post(`/events/${eventId}/registration/sends`, { kind: 'calendar', audienceKey: audience, recipients }),
    { onSuccess: refresh },
  );
  return (
    <Shell title="Calendar invites" eventId={eventId}>
      {(d) => (
        <>
          <p className="text-[15px] leading-[22px] text-ink-2 mb-3 max-w-[72ch]">
            One calendar event per day the audience attends, in Toronto time, English then French. Attach the file to
            your own email; the platform doesn&apos;t send it.
          </p>
          <table className={`${stackTable} max-w-[820px]`}>
            <thead className={stackHead}>
              <tr className={headRow}>
                <th className={th}>Audience</th>
                <th className={th}>Days</th>
                <th className={th}>Sent</th>
                <th className={th}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className={stackBody}>
              {d.audiences.map((a) => {
                const days = a.days?.length ? a.days : eventDays;
                const sends = (d.sendLog ?? [])
                  .filter((s) => s.kind === 'calendar' && s.audienceKey === a.key)
                  .sort((x, y) => y.sentOn.localeCompare(x.sentOn));
                const last = sends[0];
                return (
                  <tr key={a.key} className={stackRow}>
                    <Cell>
                      <span className="font-semibold">{a.name}</span>
                    </Cell>
                    <Cell label="Days">{days.length ? days.map(dayText).join(', ') : DASH}</Cell>
                    <Cell label="Sent">
                      {!d.visible ? (
                        DASH
                      ) : last ? (
                        <>
                          {dayText(last.sentOn)} · {plural(last.recipients, 'person', 'people')}
                          {sends.length > 1 ? <span className={em}>{plural(sends.length, 'time')} in all</span> : null}
                        </>
                      ) : (
                        <span className="text-ink-2">Not yet</span>
                      )}
                    </Cell>
                    <ActionCell>
                      <Button size="sm" disabled={get.isLoading} onClick={() => get.mutate(a.key)}>
                        Download .ics
                      </Button>
                      {canEdit && d.visible ? (
                        <Button
                          size="sm"
                          variant="quiet"
                          disabled={mark.isLoading}
                          onClick={async () => {
                            const n = await promptAction(
                              `How many people did you send the ${a.name} invite to?`,
                              '0',
                              'Mark as sent',
                            );
                            if (n !== null && /^\d+$/.test(n.trim()))
                              mark.mutate({ audience: a.key, recipients: Number(n.trim()) });
                          }}
                        >
                          Mark sent
                        </Button>
                      ) : null}
                    </ActionCell>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {get.error || mark.error ? (
            <div className="mt-3">
              <ErrorNotice>{errorMessage(get.error || mark.error, 'Could not prepare the invite.')}</ErrorNotice>
            </div>
          ) : null}
        </>
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------- comps and waivers (A14)

interface InviteeHit {
  id: string;
  person: { name: string | null; email: string | null };
}

function CompDialog({ eventId, d, onDone }: { eventId: string; d: Registration; onDone: () => void }) {
  const [form, setForm] = useState({
    kind: 'comp_ticket',
    reason: 'partner',
    group: '',
    quantity: '1',
    ticketTypeKey: '',
    value: '',
    approvedBy: '',
    code: '',
    note: '',
  });
  const [search, setSearch] = useState('');
  const [person, setPerson] = useState<InviteeHit | null>(null);
  const hits = useQuery(
    ['event-invitee-search', eventId, search],
    async () =>
      (await api.get(`/events/${eventId}/invitees`, { params: { q: search } })).data.data.invitees as InviteeHit[],
    { enabled: search.trim().length >= 2 },
  );
  const refresh = useRefresh(eventId);
  const save = useMutation(
    async () => {
      const value = form.value === '' ? null : toCents(form.value);
      if (Number.isNaN(value)) throw new Error('Value: enter an amount like 75.00');
      return api.post(`/events/${eventId}/registration/comps`, {
        kind: form.kind,
        reason: form.reason,
        inviteeId: person?.id ?? null,
        group: person ? null : form.group || null,
        quantity: Number(form.quantity || 1),
        ticketTypeKey: form.ticketTypeKey || null,
        valueCents: value,
        approvedBy: form.approvedBy || null,
        code: form.code || null,
        note: form.note || null,
      });
    },
    {
      onSuccess: () => {
        refresh();
        onDone();
      },
    },
  );
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  return (
    <Modal
      title="Complimentary tickets or waivers"
      why="Record tickets given free or fees waived, each with a reason and who approved it."
      onClose={onDone}
      width={680}
      footer={
        <Footer label="Add" busy={save.isLoading} error={save.error} onSave={() => save.mutate()} onCancel={onDone} />
      }
    >
      <div className={grid2}>
        <Field title="What">
          <select className={inputClass} value={form.kind} onChange={set('kind')}>
            {Object.entries(d.labels.compKinds).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Why">
          <select className={inputClass} value={form.reason} onChange={set('reason')}>
            {Object.entries(d.labels.compReasons).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Ticket type">
          <select className={inputClass} value={form.ticketTypeKey} onChange={set('ticketTypeKey')}>
            <option value="">–</option>
            {d.tickets.types.map((t) => (
              <option key={t.key} value={t.key}>
                {t.name}
              </option>
            ))}
          </select>
        </Field>
        <Field title="How many">
          <input
            className={`${inputClass} text-right tabular`}
            value={form.quantity}
            onChange={set('quantity')}
            inputMode="numeric"
          />
        </Field>
        {person ? (
          <div className="flex flex-col gap-[6px]">
            <span className="text-[15px] leading-5 font-semibold text-ink">A person on the list</span>
            <span className="min-h-[44px] flex items-center gap-3 text-ink">
              {person.person.name}
              <button
                type="button"
                className="text-accent underline underline-offset-[3px] hover:text-ink"
                onClick={() => setPerson(null)}
              >
                Change
              </button>
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-[6px] min-w-0">
            <Field title="A person on the list" hint="Search by name or email">
              <input
                className={inputClass}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name or email"
              />
            </Field>
            {(hits.data ?? []).slice(0, 6).map((h) => (
              <button
                key={h.id}
                type="button"
                className="text-left text-accent underline underline-offset-[3px] hover:text-ink"
                onClick={() => setPerson(h)}
              >
                {h.person.name} <span className="text-ink-3 no-underline">{h.person.email ?? ''}</span>
              </button>
            ))}
          </div>
        )}
        {!person ? (
          <Field title="…or a group" hint="An organization or course, not a name">
            <input className={inputClass} value={form.group} onChange={set('group')} />
          </Field>
        ) : (
          <div />
        )}
        <Field title="Value each ($)" hint="If not the ticket price">
          <input
            className={`${inputClass} text-right tabular`}
            value={form.value}
            onChange={set('value')}
            inputMode="decimal"
          />
        </Field>
        <Field title="Approved by">
          <input className={inputClass} value={form.approvedBy} onChange={set('approvedBy')} placeholder="e.g. NPI" />
        </Field>
        <Field title="Eventbrite code">
          <input className={inputClass} value={form.code} onChange={set('code')} />
        </Field>
        <Field title="Note">
          <input className={inputClass} value={form.note} onChange={set('note')} />
        </Field>
      </div>
    </Modal>
  );
}

/** Complimentary tickets and fee waivers, each with a reason. */
export function CompsPanel({ eventId, canEdit }: SimplePanelProps) {
  const [adding, setAdding] = useState(false);
  const refresh = useRefresh(eventId);
  const cancel = useMutation(
    async (c: Comp) => api.patch(`/events/${eventId}/registration/comps/${c.id}`, { cancelled: !c.cancelledAt }),
    { onSuccess: refresh },
  );
  const q = useRegistration(eventId);
  const c = q.data?.counts.comps;
  if (q.data && !c?.tickets && !c?.waivers && !q.data.comps?.length)
    return canEdit && q.data.visible ? (
      <>
        <AddLink label="Complimentary tickets or waivers" onClick={() => setAdding(true)} />
        {adding ? <CompDialog eventId={eventId} d={q.data} onDone={() => setAdding(false)} /> : null}
      </>
    ) : null;
  return (
    <Shell
      title="Complimentary tickets and waivers"
      eventId={eventId}
      action={(d) =>
        canEdit && d.visible ? (
          <Button size="sm" onClick={() => setAdding(true)}>
            Add
          </Button>
        ) : null
      }
    >
      {(d) => {
        const c = d.counts.comps;
        return (
          <>
            {adding ? <CompDialog eventId={eventId} d={d} onDone={() => setAdding(false)} /> : null}
            <p className="text-[15px] leading-[22px] text-ink mb-3 max-w-[72ch]">
              {plural(c.tickets, 'complimentary ticket')} and {plural(c.waivers, 'fee waiver')}
              {c.byReason.length ? `: ${c.byReason.map((r) => `${r.label} ${r.count}`).join(', ')}` : ''}.
              {d.visible ? ` Worth ${money(c.valueCents)}.` : ''}
            </p>
            {d.visible ? (
              <table className={stackTable}>
                <thead className={stackHead}>
                  <tr className={headRow}>
                    <th className={th}>For</th>
                    <th className={th}>What</th>
                    <th className={th}>Why</th>
                    <th className={th}>Approved by</th>
                    <th className={th}>Code</th>
                    <th className={th}>
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className={stackBody}>
                  {(d.comps ?? []).map((x) => {
                    const who = x.person ?? x.group ?? 'Not named';
                    return (
                      <tr key={x.id} className={`${stackRow} ${x.cancelledAt ? '[&_td]:text-ink-3' : ''}`}>
                        <Cell>
                          <span className="font-semibold">{who}</span>
                          {x.cancelledAt ? <span className={em}>Cancelled</span> : null}
                        </Cell>
                        <Cell label="What">
                          {x.quantity} × {d.labels.compKinds[x.kind]}
                          {x.ticketTypeKey ? (
                            <span className={em}>
                              {d.tickets.types.find((t) => t.key === x.ticketTypeKey)?.name ?? 'Removed ticket type'}
                            </span>
                          ) : null}
                        </Cell>
                        <Cell label="Why">
                          {d.labels.compReasons[x.reason]}
                          {x.note ? <span className={em}>{x.note}</span> : null}
                        </Cell>
                        <Cell label="Approved by">{x.approvedBy ?? DASH}</Cell>
                        <Cell label="Code">{x.code ?? DASH}</Cell>
                        <ActionCell>
                          {canEdit ? (
                            <MoreMenu
                              label={`More for ${who}`}
                              items={[
                                {
                                  label: x.cancelledAt ? 'Restore' : 'Cancel',
                                  destructive: !x.cancelledAt,
                                  disabled: cancel.isLoading,
                                  onClick: () => cancel.mutate(x),
                                },
                              ]}
                            />
                          ) : null}
                        </ActionCell>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : null}
          </>
        );
      }}
    </Shell>
  );
}

// ---------------------------------------------------------------- partners (A5)

function PartnerDialog({
  eventId,
  d,
  partner,
  days,
  onDone,
}: {
  eventId: string;
  d: Registration;
  partner?: Partner;
  days: string[];
  onDone: () => void;
}) {
  const dl = partner?.delegates;
  const [form, setForm] = useState({
    name: partner?.name ?? '',
    thinkTankSeats: partner?.thinkTankSeats == null ? '' : String(partner.thinkTankSeats),
    notes: partner?.notes ?? '',
    eventName: dl?.eventName ?? '',
    groupCode: dl?.groupCode ?? '',
    perDelegate: dollarsIn(dl?.perDelegateCents),
    seatsReserved: dl?.seatsReserved == null ? '' : String(dl.seatsReserved),
    registered: dl?.registered == null ? '' : String(dl.registered),
    invoiced: dollarsIn(dl?.invoicedCents),
    creditNote: dollarsIn(dl?.creditNoteCents),
    vendorId: dl?.vendorId ?? '',
  });
  const [meals, setMeals] = useState<{ date: string; meal: string }[]>(partner?.sharedMeals ?? []);
  const [headcount, setHeadcount] = useState<Record<string, string>>(
    Object.fromEntries(days.map((x) => [x, partner?.headcount?.[x] ? String(partner.headcount[x]) : ''])),
  );
  const [sessions, setSessions] = useState<string[]>(partner?.sharedSessionIds ?? []);
  const refresh = useRefresh(eventId);
  const save = useMutation(
    async () => {
      const amount = (v: string, label: string) => {
        if (v === '') return null;
        const n = toCents(v);
        if (Number.isNaN(n)) throw new Error(`${label}: enter an amount like 250.00`);
        return n;
      };
      const n = (v: string) => (v === '' ? null : Number(v));
      const body = {
        name: form.name,
        thinkTankSeats: n(form.thinkTankSeats),
        notes: form.notes || null,
        sharedMeals: meals,
        sharedSessionIds: sessions,
        headcount: Object.fromEntries(
          Object.entries(headcount)
            .filter(([, v]) => v !== '')
            .map(([k, v]) => [k, Number(v)]),
        ),
        delegates: {
          eventName: form.eventName || null,
          groupCode: form.groupCode || null,
          perDelegateCents: amount(form.perDelegate, 'Fee per delegate'),
          seatsReserved: n(form.seatsReserved),
          registered: n(form.registered),
          invoicedCents: amount(form.invoiced, 'Invoiced'),
          creditNoteCents: amount(form.creditNote, 'Credit note'),
          vendorId: form.vendorId || null,
        },
      };
      return partner
        ? api.patch(`/events/${eventId}/registration/partners/${partner.id}`, body)
        : api.post(`/events/${eventId}/registration/partners`, body);
    },
    {
      onSuccess: () => {
        refresh();
        onDone();
      },
    },
  );
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  const hasMeal = (date: string, meal: string) => meals.some((m) => m.date === date && m.meal === meal);
  const numIn = `${inputClass} text-right tabular`;
  return (
    <Modal
      title={partner ? `Edit ${partner.name}` : 'Co-hosting partner'}
      why="A partner's guests at shared meals are added to catering; the registrations we pay for at their event are tracked here."
      onClose={onDone}
      width={760}
      footer={
        <Footer
          label={partner ? 'Save' : 'Add the partner'}
          busy={save.isLoading}
          error={save.error}
          missing={form.name.trim() ? null : 'Give the partner’s name.'}
          onSave={() => save.mutate()}
          onCancel={onDone}
        />
      }
    >
      <div className={grid2}>
        <Field title="Partner">
          <input className={inputClass} value={form.name} onChange={set('name')} />
        </Field>
        <Field title="Think-tank seats">
          <input className={numIn} value={form.thinkTankSeats} onChange={set('thinkTankSeats')} inputMode="numeric" />
        </Field>
      </div>
      {days.length ? (
        <fieldset className="mt-6 min-w-0">
          <legend className="text-[15px] leading-5 font-semibold text-ink mb-1">Shared meals</legend>
          <p className="text-small text-ink-3 mb-2">The partner&apos;s headcount that day is added to catering.</p>
          <table className="w-full border-collapse max-md:block">
            <thead className="max-md:hidden">
              <tr className={headRow}>
                <th className={`${th} w-[130px]`}>Day</th>
                <th className={`${th} w-[110px] ${num}`}>Their people</th>
                <th className={th}>Meals shared</th>
              </tr>
            </thead>
            <tbody className="max-md:block">
              {days.map((day) => (
                <tr key={day} className="border-b border-line max-md:block max-md:py-2">
                  <td className={`${td} max-md:block max-md:py-1 font-semibold whitespace-nowrap`}>{dayText(day)}</td>
                  <td className={`${td} max-md:block max-md:py-1`}>
                    <input
                      className={`${numIn} max-md:!w-[120px]`}
                      value={headcount[day] ?? ''}
                      onChange={(e) => setHeadcount({ ...headcount, [day]: e.target.value })}
                      placeholder="People"
                      inputMode="numeric"
                      aria-label={`Their people on ${dayText(day)}`}
                    />
                  </td>
                  <td className={`${td} max-md:block max-md:py-1`}>
                    <div className="flex flex-wrap gap-x-5 gap-y-1">
                      {Object.entries(MEAL_LABEL).map(([meal, label]) => (
                        <Check
                          key={meal}
                          checked={hasMeal(day, meal)}
                          onChange={(on) =>
                            setMeals(
                              on
                                ? [...meals, { date: day, meal }]
                                : meals.filter((m) => !(m.date === day && m.meal === meal)),
                            )
                          }
                        >
                          {label}
                        </Check>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </fieldset>
      ) : null}
      {(d.sessions ?? []).length ? (
        <div className={`${grid2} mt-6`}>
          <Checks legend="Shared sessions">
            {(d.sessions ?? []).map((s) => (
              <Check
                key={s.id}
                checked={sessions.includes(s.id)}
                onChange={(on) => setSessions(on ? [...sessions, s.id] : sessions.filter((x) => x !== s.id))}
              >
                {s.title}
              </Check>
            ))}
          </Checks>
        </div>
      ) : null}
      <h3 className={subhead}>Registrations at the partner&apos;s event that we pay for</h3>
      <div className={grid2}>
        <Field title="Their event">
          <input className={inputClass} value={form.eventName} onChange={set('eventName')} />
        </Field>
        <Field title="Group code">
          <input className={inputClass} value={form.groupCode} onChange={set('groupCode')} />
        </Field>
        <Field title="Fee per delegate ($)">
          <input className={numIn} value={form.perDelegate} onChange={set('perDelegate')} inputMode="decimal" />
        </Field>
        <Field title="Seats reserved">
          <input className={numIn} value={form.seatsReserved} onChange={set('seatsReserved')} inputMode="numeric" />
        </Field>
        <Field title="Delegates registered">
          <input className={numIn} value={form.registered} onChange={set('registered')} inputMode="numeric" />
        </Field>
        <Field title="Invoiced ($)">
          <input className={numIn} value={form.invoiced} onChange={set('invoiced')} inputMode="decimal" />
        </Field>
        <Field title="Credit note ($)">
          <input className={numIn} value={form.creditNote} onChange={set('creditNote')} inputMode="decimal" />
        </Field>
        <Field title="Vendor" hint="From Budget & vendors">
          <select className={inputClass} value={form.vendorId} onChange={set('vendorId')}>
            <option value="">–</option>
            {(d.vendors ?? []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </Field>
        <Field title="Notes" wide>
          <input className={inputClass} value={form.notes} onChange={set('notes')} />
        </Field>
      </div>
    </Modal>
  );
}

function PartnerRow({
  eventId,
  d,
  p,
  canEdit,
  onEdit,
}: {
  eventId: string;
  d: Registration;
  p: Partner;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const refresh = useRefresh(eventId);
  const remove = useMutation(async () => api.delete(`/events/${eventId}/registration/partners/${p.id}`), {
    onSuccess: refresh,
  });
  const dl = p.delegates;
  const hasDelegates = Boolean(dl && (dl.eventName || dl.perDelegateCents != null));
  return (
    <tr className={stackRow}>
      <Cell>
        <span className="font-semibold">{p.name}</span>
        <span className={em}>Think-tank seats: {p.thinkTankSeats ?? '–'}</span>
        {p.notes ? <span className={em}>{p.notes}</span> : null}
      </Cell>
      <Cell label="Meals shared">
        {p.sharedMeals.length
          ? p.sharedMeals.map((m) => (
              <span key={`${m.date}-${m.meal}`} className="block">
                {MEAL_LABEL[m.meal] ?? m.meal} {dayText(m.date)}{' '}
                <span className="text-ink-3">· {plural(p.headcount[m.date] ?? 0, 'person', 'people')}</span>
              </span>
            ))
          : DASH}
      </Cell>
      <Cell label="Sessions">
        {p.sharedSessions || DASH}
        {p.sessions?.length ? <span className={em}>{p.sessions.map((s) => s.title).join(', ')}</span> : null}
      </Cell>
      <Cell label="Delegates">
        {hasDelegates && dl ? (
          <>
            {dl.registered ?? '–'} of {dl.seatsReserved ?? '–'} registered
            <span className={em}>
              {[
                dl.eventName ?? 'Their event',
                dl.groupCode ? `code ${dl.groupCode}` : null,
                `${money(dl.perDelegateCents)} each`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </>
        ) : (
          DASH
        )}
      </Cell>
      <Cell label="Owed / credit">
        {hasDelegates ? (
          <>
            {money(p.owedCents)} owed
            {p.creditDueCents ? <span className={em}>Credit note due {money(p.creditDueCents)}</span> : null}
            {p.unusedSeats ? <span className={em}>{plural(p.unusedSeats, 'unused seat')}</span> : null}
            {p.vendor ? <span className={em}>Vendor: {p.vendor.name}</span> : null}
          </>
        ) : (
          DASH
        )}
      </Cell>
      <ActionCell>
        {canEdit && d.visible ? (
          <MoreMenu
            label={`More for ${p.name}`}
            items={[
              { label: 'Edit', onClick: onEdit },
              {
                label: 'Remove',
                destructive: true,
                disabled: remove.isLoading,
                onClick: () =>
                  void confirmAction(`Remove ${p.name}?`, 'Remove the partner').then((ok) => ok && remove.mutate()),
              },
            ]}
          />
        ) : null}
      </ActionCell>
    </tr>
  );
}

/** Partner co-hosting: shared sessions and meals, their headcount in catering, registrations we pay for, think-tank seats. */
export function PartnersPanel({ eventId, canEdit }: SimplePanelProps) {
  const [editing, setEditing] = useState<Partner | 'new' | null>(null);
  const days = useEventDays(eventId);
  const q = useRegistration(eventId);
  const dialog = (d: Registration) =>
    editing ? (
      <PartnerDialog
        eventId={eventId}
        d={d}
        partner={editing === 'new' ? undefined : editing}
        days={days}
        onDone={() => setEditing(null)}
      />
    ) : null;
  if (q.data && !q.data.partners.length)
    return canEdit && q.data.visible ? (
      <>
        <AddLink label="Co-hosting partner" onClick={() => setEditing('new')} />
        {dialog(q.data)}
      </>
    ) : null;
  return (
    <Shell
      title="Partners"
      note="co-hosting"
      eventId={eventId}
      action={(d) =>
        canEdit && d.visible ? (
          <Button size="sm" onClick={() => setEditing('new')}>
            Add a partner
          </Button>
        ) : null
      }
    >
      {(d) => (
        <>
          {dialog(d)}
          <table className={stackTable}>
            <thead className={stackHead}>
              <tr className={headRow}>
                <th className={th}>Partner</th>
                <th className={th}>Meals shared</th>
                <th className={th}>Sessions</th>
                <th className={th}>Delegates</th>
                <th className={th}>Owed / credit</th>
                <th className={th}>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className={stackBody}>
              {d.partners.map((p) => (
                <PartnerRow key={p.id} eventId={eventId} d={d} p={p} canEdit={canEdit} onEdit={() => setEditing(p)} />
              ))}
            </tbody>
          </table>
        </>
      )}
    </Shell>
  );
}
